import Decimal from 'decimal.js';

/**
 * Currency Utilities - Precision-safe currency operations using Decimal.js
 *
 * Phase 2 of currency standardization (Issue #905)
 * Provides precision-safe arithmetic and formatting for all currency operations
 *
 * IMPORTANT: Always use these utilities instead of Number() conversions
 * to prevent floating-point precision loss in currency calculations.
 */

/**
 * Supported currency codes
 * Must match backend SUPPORTED_CURRENCIES in models.py
 */
export const SUPPORTED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK', 'NZD'
] as const;

export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

/**
 * Check if currency code is supported
 * @param currency - Currency code to check
 * @returns true if currency is supported
 */
export function isSupportedCurrency(currency: string): currency is SupportedCurrency {
  return SUPPORTED_CURRENCIES.includes(currency as SupportedCurrency);
}

/**
 * Currency value type (can be number, string, or Decimal)
 */
export type CurrencyValue = number | string | Decimal;

/**
 * Format price in cents to currency string
 * @param cents - Price in cents (e.g., 1000 = $10.00)
 * @param currency - Currency code (default: 'USD')
 * @returns Formatted currency string
 * @example formatPrice(1000, 'USD') // '$10.00'
 * @example formatPrice(1000, 'JPY') // '¥10' (no decimals)
 */
export function formatPrice(cents: number, currency: string = 'USD'): string {
  // Convert cents to currency units (divide by 100)
  // JPY and similar currencies don't use decimal places
  const isZeroDecimalCurrency = currency === 'JPY';
  const amount = isZeroDecimalCurrency ? cents / 100 : cents / 100;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: isZeroDecimalCurrency ? 0 : 2,
      maximumFractionDigits: isZeroDecimalCurrency ? 0 : 2,
    }).format(amount);
  } catch {
    // Fallback to basic USD format on error
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/**
 * Parse currency value to Decimal
 * @param value - Currency value (number, string, or Decimal)
 * @returns Decimal object
 */
function toDecimal(value: CurrencyValue): Decimal {
  if (value instanceof Decimal) {
    return value;
  }
  if (value === null || value === undefined || value === '') {
    return new Decimal(0);
  }
  try {
    // Remove currency symbols and commas
    const cleaned = String(value).replace(/[$,]/g, '');
    return new Decimal(cleaned);
  } catch {
    return new Decimal(0);
  }
}

/**
 * Parse currency string to Decimal
 * @param value - Currency value (number, string, or Decimal)
 * @returns Decimal object
 * @example parseCurrency('$150.00') // Decimal(150)
 * @example parseCurrency('1,234.56') // Decimal(1234.56)
 */
export function parseCurrency(value: CurrencyValue): Decimal {
  return toDecimal(value);
}

/**
 * Add multiple currency values
 *
 * @param values - Currency values to add (number, string, or Decimal)
 * @returns Sum as Decimal
 *
 * @remarks
 * - Filters out null, undefined, and empty string values
 * - Returns Decimal(0) if no valid values provided
 * - Uses Decimal.js for precision-safe arithmetic
 *
 * @example
 * addCurrency(10.01, 20.02, 30.03) // Decimal(60.06)
 * addCurrency('10.00', '', '20.00') // Decimal(30.00)
 * addCurrency(0.1, 0.2) // Decimal(0.3) (no precision loss)
 */
export function addCurrency(...values: CurrencyValue[]): Decimal {
  return values.reduce((sum, val) => {
    if (val === null || val === undefined || val === '') {
      return sum;
    }
    return sum.plus(toDecimal(val));
  }, new Decimal(0));
}

/**
 * Subtract one currency value from another
 * @param value1 - Currency value (minuend)
 * @param value2 - Currency value (subtrahend)
 * @returns Difference as Decimal
 * @example subtractCurrency(50, 10.50) // Decimal(39.50)
 * @example subtractCurrency('50.00', '10.50') // Decimal(39.50)
 */
export function subtractCurrency(value1: CurrencyValue, value2: CurrencyValue): Decimal {
  return toDecimal(value1).minus(toDecimal(value2));
}

/**
 * Multiply currency value by a number
 * @param value - Currency value
 * @param multiplier - Number to multiply by
 * @returns Product as Decimal
 * @example multiplyCurrency(10.50, 3) // Decimal(31.50)
 * @example multiplyCurrency('10.50', 3) // Decimal(31.50)
 */
export function multiplyCurrency(value: CurrencyValue, multiplier: number): Decimal {
  return toDecimal(value).times(multiplier);
}

/**
 * Divide currency value by a number
 *
 * @param value - Currency value
 * @param divisor - Number, string, or Decimal to divide by
 * @returns Quotient as Decimal (returns 0 if divisor is 0)
 *
 * @remarks
 * - Returns 0 if divisor is zero (no exception thrown)
 * - Handles repeating decimals with full precision
 *
 * @example
 * divideCurrency(100, 3) // Decimal(33.333...)
 * divideCurrency('100.00', '4') // Decimal(25)
 * divideCurrency(100, 0) // Decimal(0)
 */
export function divideCurrency(value: CurrencyValue, divisor: CurrencyValue): Decimal {
  const divisorDecimal = toDecimal(divisor);
  if (divisorDecimal.isZero()) {
    return new Decimal(0);
  }
  return toDecimal(value).dividedBy(divisorDecimal);
}

/**
 * Calculate percentage of a currency value
 * @param value - Currency value
 * @param percentage - Percentage as number (e.g., 10 for 10%)
 * @returns Percentage amount as Decimal
 * @example percentageOfCurrency(100, 15) // Decimal(15)
 * @example percentageOfCurrency('100.00', 15.5) // Decimal(15.5)
 */
export function percentageOfCurrency(value: CurrencyValue, percentage: number): Decimal {
  return toDecimal(value).times(percentage).dividedBy(100);
}

/**
 * Calculate percentage of a currency value (alias)
 * @param value - Currency value
 * @param percentage - Percentage as number (e.g., 10 for 10%)
 * @returns Percentage amount as string with 2 decimal places
 * @example calculatePercentage('100.00', 15) // '15.00'
 */
export const calculatePercentage = (value: string, percentage: number): string => {
  return percentageOfCurrency(value, percentage).toFixed(2);
};

/**
 * Apply discount to currency value
 * @param value - String currency value
 * @param discountPercentage - Discount percentage (e.g., 10 for 10% off)
 * @returns Discounted value as string with 2 decimal places
 * @example applyDiscount('100.00', 10) // '90.00'
 */
export const applyDiscount = (value: string, discountPercentage: number): string => {
  const discount = calculatePercentage(value, discountPercentage);
  return subtractCurrency(value, discount);
};

/**
 * Sum an array of currency values
 * @param values - Array of currency values
 * @returns Sum as Decimal
 * @example sumCurrency([10, 20, 30]) // Decimal(60)
 * @example sumCurrency(['10.50', '20.25', '30.75']) // Decimal(61.50)
 */
export function sumCurrency(values: CurrencyValue[]): Decimal {
  return addCurrency(...values);
}

/**
 * Compare two currency values
 *
 * @param value1 - Currency value
 * @param value2 - Currency value
 * @returns -1 if value1 < value2, 0 if equal, 1 if value1 > value2
 *
 * @remarks
 * - Handles negative zero correctly (treats -0.00 === 0.00)
 * - Empty/null values treated as 0.00
 * - Uses precision-safe comparison (no floating-point errors)
 *
 * @example
 * compareCurrency(10, 20) // -1
 * compareCurrency('20.00', '10.00') // 1
 * compareCurrency('10.00', '10.00') // 0
 */
export function compareCurrency(value1: CurrencyValue, value2: CurrencyValue): number {
  return toDecimal(value1).comparedTo(toDecimal(value2));
}

/**
 * Check if currency value is zero
 * @param value - Currency value
 * @returns true if value is zero
 * @example isZero(0) // true
 * @example isZero('0.00') // true
 */
export function isZero(value: CurrencyValue): boolean {
  return toDecimal(value).isZero();
}

/**
 * Check if currency value is positive
 * @param value - Currency value
 * @returns true if value is greater than zero
 * @example isPositive(10) // true
 * @example isPositive('10.00') // true
 */
export function isPositive(value: CurrencyValue): boolean {
  const decimal = toDecimal(value);
  return decimal.isPositive() && !decimal.isZero();
}

/**
 * Check if currency value is negative
 * @param value - Currency value
 * @returns true if value is less than zero
 * @example isNegative(-10) // true
 * @example isNegative('-10.00') // true
 */
export function isNegative(value: CurrencyValue): boolean {
  return toDecimal(value).isNegative();
}

/**
 * Get absolute value of currency
 * @param value - String currency value
 * @returns Absolute value as string with 2 decimal places
 * @example absoluteValue('-10.50') // '10.50'
 */
export const absoluteValue = (value: string): string => {
  return new Decimal(value || '0').abs().toFixed(2);
};

/**
 * Round currency value to specified decimal places
 * @param value - Currency value
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Rounded Decimal
 * @example roundCurrency(10.126) // Decimal(10.13)
 * @example roundCurrency(10.126, 1) // Decimal(10.1)
 */
export function roundCurrency(value: CurrencyValue, decimalPlaces: number = 2): Decimal {
  return toDecimal(value).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP);
}

/**
 * Convert currency value to string with specified decimal places
 * @param value - Currency value
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns String representation with fixed decimal places
 * @example currencyToString(10) // '10.00'
 * @example currencyToString(10.123, 3) // '10.123'
 */
export function currencyToString(value: CurrencyValue, decimalPlaces: number = 2): string {
  return toDecimal(value).toFixed(decimalPlaces);
}

/**
 * Convert currency value to number
 * @param value - Currency value
 * @returns Number representation
 * @example currencyToNumber('10.50') // 10.50
 * @example currencyToNumber(new Decimal('10.50')) // 10.50
 */
export function currencyToNumber(value: CurrencyValue): number {
  return toDecimal(value).toNumber();
}

/**
 * Format currency value for display
 * @param value - Currency value
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted currency string with $ symbol
 * @example formatCurrency(150) // '$150.00'
 * @example formatCurrency(150.99) // '$150.99'
 * @example formatCurrency(150.123, 3) // '$150.123'
 */
export function formatCurrency(value: CurrencyValue, decimalPlaces: number = 2): string {
  try {
    const decimal = toDecimal(value);
    if (decimal.isNaN()) {
      return '$0.00';
    }
    return '$' + decimal.toFixed(decimalPlaces);
  } catch {
    return '$0.00';
  }
}

/**
 * Format currency value without symbol
 * @param value - String currency value
 * @returns Formatted number string with commas
 * @example formatCurrencyWithoutSymbol('1234.56') // '1,234.56'
 */
export const formatCurrencyWithoutSymbol = (value: string): string => {
  try {
    const numValue = new Decimal(value || '0').toNumber();
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  } catch {
    // Fallback to raw value on error
    return value || '0.00';
  }
};

/**
 * Validate currency string format
 * @param value - String to validate
 * @returns true if valid currency format (e.g., '10.50', '0.00', '1234.56')
 * @example isValidCurrency('10.50') // true
 * @example isValidCurrency('10.5') // false (needs 2 decimal places)
 */
export const isValidCurrency = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  // Allow optional negative sign, digits, optional decimal point with exactly 2 digits
  return /^-?\d+(\.\d{2})?$/.test(value);
};

/**
 * Validate currency input (allows partial input during typing)
 * @param value - String to validate
 * @returns true if valid during input (e.g., '10', '10.', '10.5')
 * @example isValidCurrencyInput('10.5') // true
 * @example isValidCurrencyInput('10.abc') // false
 */
export const isValidCurrencyInput = (value: string): boolean => {
  if (!value) return true; // Allow empty
  // Allow digits, optional decimal point, and 0-2 decimal digits
  return /^\d*\.?\d{0,2}$/.test(value);
};

/**
 * Sanitize currency input to prevent injection attacks
 *
 * @param value - Input value (can be number, string, or undefined)
 * @returns Sanitized string containing only valid currency characters
 *
 * @remarks
 * - Detects and properly handles scientific notation (e.g., "1e10" -> "10000000000.00")
 * - Strips all non-numeric characters except digits, decimal point, and minus sign
 * - Ensures only one decimal point (keeps first occurrence)
 * - Ensures minus sign only at the beginning
 * - Does NOT validate format (use isValidCurrency for that)
 * - Returns empty string for null, undefined, or empty input
 *
 * @example
 * sanitizeCurrencyInput('$10.50') // '10.50'
 * sanitizeCurrencyInput('10.50<script>') // '10.50'
 * sanitizeCurrencyInput('-10.50') // '-10.50'
 * sanitizeCurrencyInput('10..50') // '10.50' (only first decimal kept)
 * sanitizeCurrencyInput('1e10') // '10000000000.00' (scientific notation)
 * sanitizeCurrencyInput(null) // ''
 * sanitizeCurrencyInput(undefined) // ''
 */
export const sanitizeCurrencyInput = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const stringValue = String(value);

  // Check for scientific notation (e.g., "1e10", "5.5e-3")
  // This must be done BEFORE stripping characters to avoid data loss
  if (/[eE]/.test(stringValue)) {
    try {
      // Parse through Decimal to handle scientific notation correctly
      const decimal = new Decimal(stringValue);
      return decimal.toFixed(2);
    } catch {
      // If parsing fails, fall through to normal sanitization
    }
  }

  // Remove all characters except digits, decimal point, and minus sign
  const sanitized = stringValue.replace(/[^\d.-]/g, '');

  // Ensure only one decimal point
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }

  // Ensure minus sign only at the beginning
  const minusCount = (sanitized.match(/-/g) || []).length;
  if (minusCount > 0) {
    const isNegative = sanitized[0] === '-';
    const digitsAndDecimal = sanitized.replace(/-/g, '');
    return isNegative ? '-' + digitsAndDecimal : digitsAndDecimal;
  }

  return sanitized;
};

/**
 * Parse currency input to standard format
 * @param value - Input value (can be number, string, or undefined)
 * @returns Standardized currency string with 2 decimal places
 * @example parseCurrencyInput('10') // '10.00'
 * @example parseCurrencyInput(10.5) // '10.50'
 * @example parseCurrencyInput('') // '0.00'
 */
export const parseCurrencyInput = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') {
    return '0.00';
  }
  try {
    const sanitized = sanitizeCurrencyInput(value);
    if (!sanitized) return '0.00';
    return new Decimal(sanitized).toFixed(2);
  } catch {
    // Return safe default on parsing error
    return '0.00';
  }
};


/**
 * Get minimum of multiple currency values
 * @param values - Array of string currency values
 * @returns Minimum value as string with 2 decimal places
 * @example minCurrency('10.00', '20.00', '5.00') // '5.00'
 */
export const minCurrency = (...values: string[]): string => {
  if (values.length === 0) return '0.00';
  return Decimal.min(...values.map(v => new Decimal(v || '0'))).toFixed(2);
};

/**
 * Get maximum of multiple currency values
 * @param values - Array of string currency values
 * @returns Maximum value as string with 2 decimal places
 * @example maxCurrency('10.00', '20.00', '5.00') // '20.00'
 */
export const maxCurrency = (...values: string[]): string => {
  if (values.length === 0) return '0.00';
  return Decimal.max(...values.map(v => new Decimal(v || '0'))).toFixed(2);
};

/**
 * Calculate average of multiple currency values
 * @param values - Array of string currency values
 * @returns Average value as string with 2 decimal places
 * @example averageCurrency('10.00', '20.00', '30.00') // '20.00'
 */
export const averageCurrency = (...values: string[]): string => {
  if (values.length === 0) return '0.00';
  const sum = addCurrency(...values);
  return divideCurrency(sum, values.length);
};
